
"use client"

import { useTrack } from 'bwax-ui/track';
import { useEffect } from 'react';

import Loading from "bwax-ui/components/Loading";
import { useState } from 'react';

export default function Error({ error, reset }) {

    function getErrorDetails() {
        const details = {
            timestamp: new Date().toISOString(),
            url: typeof window !== 'undefined' ? window.location.href : 'N/A',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
            errorName: error?.name || 'Unknown Error',
            errorMessage: error?.message || 'No message',
            errorStack: error?.stack || '',
            errorDigest: error?.digest || '', // Next.js 特有
            componentStack: error?.componentStack || '', // React 特有
            errorString: error?.toString() || '',
        };
        return details;
    }

    function printError() {
        const details = getErrorDetails();
        
        let output = '';
        output += `=== 错误详情 ===\n`;
        output += `时间: ${details.timestamp}\n`;
        output += `页面: ${details.url}\n`;
        output += `浏览器: ${details.userAgent}\n`;
        output += `\n`;
        output += `=== 错误信息 ===\n`;
        output += `类型: ${details.errorName}\n`;
        output += `消息: ${details.errorMessage}\n`;
        if (details.errorDigest) {
            output += `Digest: ${details.errorDigest}\n`;
        }
        output += `\n`;
        output += `=== 错误堆栈 ===\n`;
        output += details.errorStack || details.errorString;
        if (details.componentStack) {
            output += `\n\n=== React 组件堆栈 ===\n`;
            output += details.componentStack;
        }
        
        return output;
    }

    const [shouldRenderError, setShouldRenderError] = useState(true);
    const [showDetails, setShowDetails] = useState(true);
    const [copied, setCopied] = useState(false);

    const track = useTrack();
    useEffect(() => {
        const errorDetails = getErrorDetails();
        track("global_error", { 
            error: printError(),
            errorName: errorDetails.errorName,
            errorMessage: errorDetails.errorMessage,
            url: errorDetails.url,
            userAgent: errorDetails.userAgent,
        });

        console.error('=== Global Error Caught ===');
        console.error('Error:', error);
        console.error('Error Details:', errorDetails);

        if (typeof (window) !== "undefined") {
            // 可以考虑在 localStorage 计数，避免陷入死循环
            if(!shouldRenderError) {
                // window.location.reload();
                // location.href = location.href
            }
            
        }

    }, [error]);

    function handleRefresh() {
        if (typeof window !== "undefined") {
            window.location.reload();
        }
    }

    function handleReset() {
        if (reset) {
            reset();
        }
    }

    async function handleCopyError() {
        const errorText = printError();
        if (!errorText) return;

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(errorText);
            } else {
                // 降级方案：使用 textarea 复制
                const textarea = document.createElement('textarea');
                textarea.value = errorText;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('复制失败:', err);
        }
    }

    function renderError() {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-violet-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 border border-violet-200">
                    {/* 错误图标 */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">页面出现错误</h1>
                        <p className="text-gray-600">很抱歉，页面遇到了一些问题，请尝试刷新页面或联系管理员</p>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex justify-center mb-6">
                        <button
                            onClick={handleRefresh}
                            className="bg-violet-600 hover:bg-violet-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            刷新页面
                        </button>
                    </div>

                    {/* 错误详情切换 */}
                    {printError() && (
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <button
                                    onClick={() => setShowDetails(!showDetails)}
                                    className="text-gray-600 hover:text-gray-800 font-medium flex items-center gap-2 transition-colors duration-200"
                                >
                                    <svg 
                                        className={`w-4 h-4 transition-transform duration-200 ${showDetails ? 'rotate-90' : ''}`} 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                    {showDetails ? '隐藏' : '显示'}错误详情
                                </button>
                                
                                <button
                                    onClick={handleCopyError}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-700 font-medium transition-colors duration-200"
                                >
                                    {copied ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            已复制
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            复制错误信息
                                        </>
                                    )}
                                </button>
                            </div>
                            
                            {showDetails && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <pre className="text-xs text-gray-600 overflow-auto max-h-96 whitespace-pre-wrap break-words font-mono">
                                        {printError()}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 联系信息 */}
                    <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                        <p className="text-sm text-gray-500">
                            如果问题持续存在，请联系客服
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    function renderLoading() {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-violet-100 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center">
                    <Loading />
                    <p className="mt-4 text-gray-600">正在处理中...</p>
                </div>
            </div>
        )
    }

    return (
        <html>
            <head>
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <title>页面错误</title>
                <script src="https://cdn.tailwindcss.com" async></script>
            </head>
            <body>
                {shouldRenderError ? renderError() : renderLoading()}
            </body>
        </html>

    )
}
